import { h } from '@omni-torn/ui';
import {
  FACTION_APPS_TERMS,
  positionRank,
  readMembersActivity,
  rosterRows,
  rosterSummary,
  type FactionApplication,
  type FactionApplicationsAnswer,
  type FactionRosterAnswer,
  type MemberActivityShare,
  type RosterExtra,
  type RosterRow,
  type WarEstimatesAnswer,
} from '@omni-torn/shared';
import type { FactionMember } from '../read';
import { server } from '../session';
import { csvButton, filterSelect } from '../log-kit/ui';
import { ago, box, duration, ext, loading, num, problem, profileUrl, short, table, tct } from '../ui';

/**
 * Faction ops II (website roadmap S20): Faction → Roster (TornStats' roster, Torn Intel's members overview) and
 * Applications (Torn Intel's applications with each applicant's estimate).
 *
 * - Roster: your own key's member list (the page's read) with what a member's Discord-bot key reads of Torn's v2 list
 *   (revive setting, in an OC, on a wall — /api/faction/ops/roster), Omni-Torn's estimates from the player database
 *   (/api/faction/war/estimates, estimates only) and each member's 30-day activity from the activity tracker (S22,
 *   /api/faction/activity/members — read leniently; not there yet = not shown).
 * - Applications: /api/faction/ops/applications — officers only (leader, co-leader, members whose bot key has the
 *   faction's API access); read only with a key whose owner agreed to terms version FACTION_APPS_TERMS.
 *
 * The faction page draws itself again every 20 s: each part is built once per visit and handed back (filters stay).
 */

const built = new Map<string, HTMLElement>();
/** A new visit to the Faction page starts these afresh. */
export function resetRoster(): void {
  built.clear();
}
const once = (key: string, make: () => HTMLElement): HTMLElement => {
  let el = built.get(key);
  if (!el) built.set(key, (el = make()));
  return el;
};

type Estimate = WarEstimatesAnswer['estimates'][number];
const METHOD: Record<string, string> = { fight: 'from fights', pool: 'from the reading pool', rank: 'a guess from their rank', spy: 'from a spy' };
const estimateCell = (e: { total: number; source: string; at: number } | null | undefined): Node => (e ? h('span', { title: `Omni-Torn’s estimate, ${METHOD[e.source] ?? e.source}${e.at ? `, ${ago(e.at)}` : ''}`, class: e.source === 'rank' ? 'muted' : '' }, `${e.source === 'rank' ? '~' : ''}${short(e.total)}`) : h('span', { class: 'muted' }, '·'));
const yesNo = (v: boolean | null | undefined, yes: string, title: string): Node | string => (v ? h('span', { class: 't-green', title }, yes) : v === false ? h('span', { class: 'muted' }, '—') : h('span', { class: 'muted' }, '·'));
const activityCell = (a: MemberActivityShare | null): Node =>
  a ? h('span', { title: `Seen online or idle${a.ratio !== null ? ` ${Math.round(a.ratio * 100)}% of the time` : ''}${a.activeHours !== null ? `, about ${num(a.activeHours)} hours` : ''} in the last 30 days` }, a.ratio !== null ? `${Math.round(a.ratio * 100)}%` : `${num(a.activeHours)} h`) : h('span', { class: 'muted' }, '·');

const SEEN: ReadonlyArray<readonly [string, string]> = [
  ['', 'Any time'],
  ['day', 'Active in the last day'],
  ['week', 'Active in the last 7 days'],
  ['idle7', 'Not active for 7+ days'],
];

// ---------------------------------------------------------------- roster

/** Faction → Roster. `members`: the page's own-key list (freshest statuses). */
export function rosterSection(members: () => FactionMember[], factionId: number | null): HTMLElement {
  return once('roster', () => {
    const holder = h('div', { 'data-roster': 'body' }, loading('Reading the roster…'));
    let extras: RosterExtra[] = [];
    let extrasNote = '';
    let estimates: Estimate[] = [];
    let activity = new Map<number, MemberActivityShare>();
    let position = '';
    let seen = '';
    const out = h('div', {});
    const rows = (): RosterRow[] => rosterRows(members(), extras, estimates, activity);
    const pick = (r: RosterRow, now: number) =>
      (!position || r.position === position) &&
      (!seen || (seen === 'idle7' ? r.lastActionAt === null || now - r.lastActionAt >= 7 * 86_400_000 : r.lastActionAt !== null && now - r.lastActionAt < (seen === 'day' ? 1 : 7) * 86_400_000));
    const draw = () => {
      const now = Date.now();
      const all = rows();
      const list = all.filter((r) => pick(r, now));
      const s = rosterSummary(list, now);
      out.replaceChildren(
        h('p', { class: 'muted small', 'data-roster': 'summary' }, `${num(s.members)} member${s.members === 1 ? '' : 's'} · ${num(s.avgDays)} days in the faction on average · ${num(s.activeDay)} active in the last day · ${num(s.estimated)} with an estimate${extras.length ? ` · ${num(s.inOc)} in an OC · ${num(s.revivable)} revivable` : ''}`),
        table(
          list,
          [
            { label: 'Member', get: (r) => ext(profileUrl(r.id), r.name || `#${r.id}`), sort: (r) => r.name.toLowerCase() },
            { label: 'Position', get: (r) => r.position || '—', sort: (r) => `${positionRank(r.position)}${r.position.toLowerCase()}` },
            { label: 'Lvl', get: (r) => String(r.level), sort: (r) => r.level, cls: 'r' },
            { label: 'Days', get: (r) => num(r.days), sort: (r) => r.days, cls: 'r' },
            { label: 'Last action', get: (r) => h('span', { class: `dot dot-${r.online.toLowerCase()}`, title: r.online }, r.lastAction || '—'), sort: (r) => -(r.lastActionAt ?? 0) },
            { label: '30-day activity', get: (r) => activityCell(r.activity), sort: (r) => r.activity?.ratio ?? (r.activity?.activeHours ?? -1) / 720, cls: 'r' },
            { label: 'Estimate', get: (r) => estimateCell(r.estimate), sort: (r) => r.estimate?.total ?? -1, cls: 'r' },
            { label: 'Revives', get: (r) => (r.extra?.reviveSetting ? h('span', { class: r.extra.revivable ? '' : 'muted' }, r.extra.reviveSetting) : h('span', { class: 'muted' }, '·')), sort: (r) => r.extra?.reviveSetting ?? '' },
            { label: 'OC', get: (r) => yesNo(r.extra?.inOc, 'in one', 'In an organised crime now'), sort: (r) => Number(r.extra?.inOc ?? -1), cls: 'c' },
            { label: 'Wall', get: (r) => yesNo(r.extra?.onWall, 'on a wall', 'Defending a territory wall now'), sort: (r) => Number(r.extra?.onWall ?? -1), cls: 'c' },
          ],
          { initial: 1, desc: false, empty: 'No one here.', remember: 'faction-roster', csv: false },
        ),
        h(
          'p',
          { class: 'muted small', 'data-roster': 'notes' },
          'Positions, days, levels and last action: Torn, with your key. Estimates: Omni-Torn’s player database (· = none yet). ',
          extras.length ? 'Revives, OC and wall: Torn’s member list read with a key a member gave the Discord bot (kept an hour). ' : extrasNote ? `Revives, OC and wall: ${extrasNote} ` : '',
          activity.size ? '30-day activity: Omni-Torn’s activity tracker.' : '30-day activity shows once Omni-Torn tracks your faction’s activity.',
        ),
      );
    };
    const positions = () => [...new Set(members().map((m) => m.position).filter(Boolean))].sort((a, b) => positionRank(a) - positionRank(b) || a.localeCompare(b));
    const tools = h(
      'div',
      { class: 'toolbar' },
      filterSelect('Position', [['', 'Every position'], ...positions().map((p) => [p, p] as const)], position, (v) => ((position = v), draw()), 'roster-position'),
      filterSelect('Seen', SEEN, seen, (v) => ((seen = v), draw()), 'roster-seen'),
      csvButton(
        () => 'faction-roster.csv',
        () => ({
          header: ['id', 'name', 'position', 'level', 'days in faction', 'last action (UTC)', '30-day activity %', 'estimate', 'estimate from', 'revive setting', 'in OC', 'on wall'],
          rows: rows()
            .filter((r) => pick(r, Date.now()))
            .map((r) => [r.id, r.name, r.position, r.level, r.days, r.lastActionAt ? new Date(r.lastActionAt).toISOString() : '', r.activity?.ratio !== null && r.activity?.ratio !== undefined ? Math.round(r.activity.ratio * 100) : '', r.estimate?.total ?? '', r.estimate?.source ?? '', r.extra?.reviveSetting ?? '', r.extra?.inOc === null || r.extra?.inOc === undefined ? '' : r.extra.inOc ? 'yes' : 'no', r.extra?.onWall === null || r.extra?.onWall === undefined ? '' : r.extra.onWall ? 'yes' : 'no']),
        }),
      ),
    );
    const ids = members().map((m) => m.id).slice(0, 200);
    void Promise.all([
      server<FactionRosterAnswer>('GET', '/api/faction/ops/roster')
        .then((a) => {
          extras = a.members;
          extrasNote = a.problem ?? '';
        })
        .catch((err: Error) => {
          extrasNote = err.message;
        }),
      ids.length
        ? server<WarEstimatesAnswer>('GET', `/api/faction/war/estimates?ids=${ids.join(',')}`)
            .then((a) => {
              estimates = a.estimates;
            })
            .catch(() => {})
        : Promise.resolve(),
      // The activity tracker (S22): not there yet, or not for you = not shown.
      server<unknown>('GET', '/api/faction/activity/members?days=30')
        .then((a) => {
          activity = readMembersActivity(a);
        })
        .catch(() => {}),
    ]).then(() => {
      draw();
      holder.replaceChildren(tools, out);
    });
    return box('Roster', holder, { id: 'roster', extra: factionId ? ext(`https://www.torn.com/factions.php?step=profile&ID=${factionId}`, 'Faction page ↗') : null });
  });
}

// ---------------------------------------------------------------- applications

const STATUS_LABEL: Record<FactionApplication['status'], string> = { active: 'Waiting', accepted: 'Accepted', declined: 'Declined', withdrawn: 'Withdrawn', other: 'Other' };

/** Faction → Applications (officers only). */
export function applicationsSection(): HTMLElement {
  return once('applications', () => {
    const holder = h('div', {}, loading('Reading applications…'));
    let which = 'active';
    const read = () =>
      server<FactionApplicationsAnswer>('GET', '/api/faction/ops/applications')
        .then((a) => {
          if (!a.officer) {
            holder.replaceChildren(h('p', { class: 'notice', 'data-apps': 'officers' }, 'Applications are shown to your faction’s leader, co-leader and members they allowed (Faction → Delegates).'));
            return;
          }
          const est = new Map(a.estimates.map((e) => [e.id, e]));
          const out = h('div', {});
          const draw = () => {
            const list = a.applications.filter((x) => !which || (which === 'active' ? x.status === 'active' : x.status !== 'active'));
            const now = Date.now() / 1000;
            out.replaceChildren(
              h('p', { class: 'muted small', 'data-apps': 'count' }, `${num(a.applications.filter((x) => x.status === 'active').length)} waiting · ${num(a.applications.length)} in all`),
              table(
                list,
                [
                  { label: 'Applicant', get: (x) => h('span', {}, ext(profileUrl(x.user.id), x.user.name), h('span', { class: 'muted small' }, ` [${x.user.id}]`)), sort: (x) => x.user.name.toLowerCase() },
                  { label: 'Lvl', get: (x) => String(x.user.level), sort: (x) => x.user.level, cls: 'r' },
                  { label: 'Estimate', get: (x) => estimateCell(est.get(x.user.id)), sort: (x) => est.get(x.user.id)?.total ?? -1, cls: 'r' },
                  {
                    label: 'Their stats',
                    get: (x) => (x.stats ? h('span', { title: `Shown to your faction in the application: strength ${num(x.stats.strength)}, speed ${num(x.stats.speed)}, dexterity ${num(x.stats.dexterity)}, defense ${num(x.stats.defense)}` }, short(x.stats.total)) : h('span', { class: 'muted' }, '·')),
                    sort: (x) => x.stats?.total ?? -1,
                    cls: 'r',
                  },
                  { label: 'Message', get: (x) => (x.message ? h('span', { class: 'small' }, x.message) : h('span', { class: 'muted' }, '—')) },
                  { label: 'Status', get: (x) => h('span', { class: x.status === 'active' ? 't-green' : 'muted' }, STATUS_LABEL[x.status]), sort: (x) => x.status },
                  { label: 'Runs out', get: (x) => (x.status === 'active' && x.validUntil > now ? h('span', { title: `${tct(x.validUntil * 1000)} TCT` }, `in ${duration(x.validUntil - now)}`) : h('span', { class: 'muted' }, '—')), sort: (x) => x.validUntil },
                  { label: 'First seen', get: (x) => h('span', { title: `${tct(x.firstSeen * 1000)} TCT` }, ago(x.firstSeen * 1000)), sort: (x) => x.firstSeen },
                ],
                { initial: 6, desc: false, empty: which === 'active' ? 'No applications waiting.' : 'None.', remember: 'faction-apps', csv: false },
              ),
            );
          };
          holder.replaceChildren(
            ...(a.problem ? [h('p', { class: 'notice', 'data-apps': 'problem' }, a.problem)] : []),
            h(
              'div',
              { class: 'toolbar' },
              filterSelect('Which', [['active', 'Waiting'], ['done', 'Answered or withdrawn'], ['', 'All']], which, (v) => ((which = v), draw()), 'apps-which'),
              csvButton(
                () => 'faction-applications.csv',
                () => ({ header: ['application', 'applicant id', 'name', 'level', 'estimate', 'stats shown (total)', 'status', 'valid until (UTC)', 'first seen (UTC)', 'message'], rows: a.applications.map((x) => [x.id, x.user.id, x.user.name, x.user.level, est.get(x.user.id)?.total ?? '', x.stats?.total ?? '', x.status, x.validUntil ? new Date(x.validUntil * 1000).toISOString() : '', new Date(x.firstSeen * 1000).toISOString(), x.message]) }),
              ),
            ),
            out,
            h(
              'p',
              { class: 'muted small', 'data-apps': 'kept' },
              `${a.readAt ? `Read from Torn ${ago(a.readAt)} with a key a member gave the Discord bot. ` : ''}`,
              a.readable ? `Kept by Omni-Torn for your faction’s officers (up to ${num(a.keepDays)} days); the stats an applicant shows your faction only from the latest read. ` : `Omni-Torn reads them once a member whose key has your faction’s “API access” gives it to the Discord bot and agrees to terms version ${FACTION_APPS_TERMS}. `,
              'Accept or decline on Torn: ',
              ext('https://www.torn.com/factions.php?step=your#/tab=controls', 'your faction’s controls ↗'),
            ),
          );
          draw();
        })
        .catch((err) => holder.replaceChildren(problem(err)));
    void read();
    return box('Applications', holder, { id: 'applications' });
  });
}
